import React, { useState } from "react";
import { FaGlobe, FaExternalLinkAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { HiOutlineDocumentText } from "react-icons/hi";
import { Content } from "@/models/document";
import { ChatTheme } from "./types";
import { safeString } from "@/utils/safe-value";
import useTranslation from "@/hooks/use-translation";

interface ChatDocumentsWidgetProps {
  documents: Content[];
  theme: ChatTheme;
  /** Max documents to show before "show more" */
  initialLimit?: number;
}

interface DocumentInfo {
  title: string;
  text: string;
  date: string;
  language: string;
  url: string;
  authorName: string;
  authorAvatar: string;
  platform: string;
}

function extractDocumentInfo(item: Content): DocumentInfo {
  const raw = item.normalized ?? (item as unknown as Record<string, unknown>);

  const authorName =
    safeString((raw as any).author?.username || (raw as any).author?.title) || "";
  const authorAvatar =
    safeString((raw as any).author?.media?.[0]?.url || (raw as any).author?.avatar) || "";

  const title = safeString((raw as any).title) || "";
  const text = safeString((raw as any).text || (raw as any).description) || "";
  const date = safeString((raw as any).date || (raw as any).published_time) || "";
  const language = safeString(
    (raw as any).language || (raw as any).lang || (item as any).extra?.lang
  ) || "";
  const url = safeString((raw as any).url) || "";

  let platform = "";
  if (url) {
    try {
      const hostname = new URL(url).hostname.replace("www.", "");
      platform = hostname.split(".")[0];
    } catch {
      // ignore
    }
  }

  return { title, text, date, language, url, authorName, authorAvatar, platform };
}

function formatRelativeDate(dateStr: string): string {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);

    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin}min`;
    if (diffH < 24) return `il y a ${diffH}h`;
    if (diffD < 7) return `il y a ${diffD}j`;

    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}

/**
 * Compact, dark-themed document cards for display in the chat.
 * Shows a collapsible list of found documents with key metadata.
 */
const ChatDocumentsWidget: React.FC<ChatDocumentsWidgetProps> = ({
  documents,
  theme,
  initialLimit = 3,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visibleDocs = expanded ? documents : documents.slice(0, initialLimit);
  const hasMore = documents.length > initialLimit;

  return (
    <div
      className={`
        w-full rounded-2xl overflow-hidden
        bg-white/[0.03] ${theme.border} border
      `}
    >
      {/* Header */}
      <div
        className={`
          flex items-center gap-2 px-4 py-2.5
          ${theme.border} border-b
        `}
      >
        <HiOutlineDocumentText className={`h-4 w-4 ${theme.textMuted}`} />
        <span className={`text-xs font-medium ${theme.textSecondary}`}>
          {t("n_documents_found", { count: documents.length })}
        </span>
      </div>

      {/* Document list */}
      <div className="divide-y divide-white/[0.06]">
        {visibleDocs.map((doc, idx) => {
          const info = extractDocumentInfo(doc);
          return (
            <DocumentRow key={doc.id || idx} info={info} theme={theme} />
          );
        })}
      </div>

      {/* Show more / less */}
      {hasMore && (
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className={`
            flex items-center justify-center gap-1.5 w-full px-4 py-2
            text-xs font-medium text-accent
            hover:bg-white/[0.04] transition-colors
            ${theme.border} border-t
          `}
        >
          {expanded ? (
            <>
              <FaChevronUp className="h-2.5 w-2.5" />
              Réduire
            </>
          ) : (
            <>
              <FaChevronDown className="h-2.5 w-2.5" />
              {t("see_n_more_documents", { count: documents.length - initialLimit })}
            </>
          )}
        </button>
      )}
    </div>
  );
};

/** Single document row */
const DocumentRow: React.FC<{ info: DocumentInfo; theme: ChatTheme }> = ({
  info,
  theme,
}) => {
  const handleClick = () => {
    if (info.url) window.open(info.url, "_blank", "noopener");
  };

  return (
    <div
      onClick={handleClick}
      className={`
        flex gap-3 px-4 py-3
        ${info.url ? "cursor-pointer hover:bg-white/[0.04]" : ""}
        transition-colors group
      `}
    >
      {/* Avatar / Platform icon */}
      <div className="flex-shrink-0 mt-0.5">
        {info.authorAvatar ? (
          <img
            src={info.authorAvatar}
            alt={info.authorName}
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div
            className={`
              flex h-8 w-8 items-center justify-center rounded-full
              bg-white/[0.08] text-xs font-semibold ${theme.textMuted}
            `}
          >
            {info.authorName
              ? info.authorName.charAt(0).toUpperCase()
              : info.platform
                ? info.platform.charAt(0).toUpperCase()
                : "D"}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top row: author + date */}
        <div className="flex items-center gap-2 mb-0.5">
          {info.authorName && (
            <span className={`text-xs font-semibold ${theme.textPrimary} truncate`}>
              {info.authorName}
            </span>
          )}
          {info.platform && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/[0.08] text-white/50 capitalize">
              {info.platform}
            </span>
          )}
          {info.date && (
            <span className={`text-[10px] ${theme.textMuted} ml-auto flex-shrink-0`}>
              {formatRelativeDate(info.date)}
            </span>
          )}
        </div>

        {/* Title */}
        {info.title && (
          <p className={`text-sm font-medium ${theme.textSecondary} line-clamp-1 mb-0.5`}>
            {info.title}
          </p>
        )}

        {/* Text preview */}
        {info.text && (
          <p className={`text-xs ${theme.textMuted} line-clamp-2 leading-relaxed`}>
            {info.text}
          </p>
        )}

        {/* Bottom row: language + link */}
        <div className="flex items-center gap-3 mt-1.5">
          {info.language && (
            <span className={`flex items-center gap-1 text-[10px] ${theme.textMuted}`}>
              <FaGlobe className="h-2.5 w-2.5" />
              {info.language.toUpperCase()}
            </span>
          )}
          {info.url && (
            <span
              className={`
                flex items-center gap-1 text-[10px] text-accent
                opacity-0 group-hover:opacity-100 transition-opacity
              `}
            >
              <FaExternalLinkAlt className="h-2 w-2" />
              Ouvrir
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

ChatDocumentsWidget.displayName = "ChatDocumentsWidget";

export default ChatDocumentsWidget;
